import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { getMemberToken } from './lib/memberToken';

/**
 * v0.15 follow-up: what the bare domain does.
 *
 * In a normal browser this is unchanged — straight to the admin login, on the
 * long-standing reasoning that someone typing the club address by hand is
 * almost always an admin or a chair.
 *
 * From an INSTALLED home-screen icon it cannot be that simple, and the reason
 * is worth writing down because it defeats the obvious fixes:
 *
 * - One app, one manifest, one `start_url` — but three audiences: members,
 *   the admin panel, and the kiosk tablet, which the club also pins. Pointing
 *   `start_url` at any one of them strands the other two.
 * - **A standalone web app on iOS gets its own storage jar, separate from
 *   Safari's.** So a member who identifies in Safari and then installs the app
 *   opens it with *empty* storage: the freshly installed app genuinely cannot
 *   know who its owner is. That is exactly the bug this fixes — the owner
 *   installed from the member screen and was met by the admin login.
 *
 * So: use evidence when there is any, and otherwise ASK, once, and remember.
 * Guessing would be wrong for two of the three audiences every time.
 */

const PREFERENCE_KEY = 'bt.landing';

type Landing = 'member' | 'admin';

function isStandalone(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

function readPreference(): Landing | undefined {
  try {
    const value = localStorage.getItem(PREFERENCE_KEY);
    return value === 'member' || value === 'admin' ? value : undefined;
  } catch {
    return undefined;
  }
}

function rememberPreference(value: Landing): void {
  try {
    localStorage.setItem(PREFERENCE_KEY, value);
  } catch {
    // Without storage the choice is asked again next time -- mildly annoying,
    // never broken.
  }
}

export default function LandingRoute() {
  const [choice, setChoice] = useState<Landing | undefined>(readPreference);

  // Already identified on this device: no question to ask.
  if (getMemberToken()) return <Navigate to="/mitglied" replace />;
  if (choice === 'member') return <Navigate to="/mitglied" replace />;
  if (choice === 'admin') return <Navigate to="/admin/login" replace />;

  // A plain browser keeps the behaviour it has always had.
  if (!isStandalone()) return <Navigate to="/admin/login" replace />;

  const pick = (value: Landing) => {
    rememberPreference(value);
    setChoice(value);
  };

  return (
    <div className="member-screen">
      <h1>Willkommen</h1>
      <p>Wie möchten Sie die App nutzen? Die Auswahl wird gemerkt.</p>
      <div className="kiosk-grid">
        <button className="kiosk-tile" onClick={() => pick('member')}>
          <span>Als Mitglied</span>
          <span className="subtitle">Eigene Getränke und Speisen erfassen</span>
        </button>
        <button className="kiosk-tile" onClick={() => pick('admin')}>
          <span>Verwaltung oder Kiosk</span>
          <span className="subtitle">Anmeldung mit E-Mail und Passwort</span>
        </button>
      </div>
      <p className="member-hint">
        Falsch gewählt? <Link to="/admin/login">Zur Anmeldung</Link> oder{' '}
        <Link to="/mitglied">zur Mitglieder-Ansicht</Link> — die Auswahl lässt sich dort jederzeit
        wechseln.
      </p>
    </div>
  );
}
